import { useEffect, useMemo, useRef, useState } from 'react';
import { PAGE_NS, pageDocument, type PageMessage } from './page-document';
import type { PageResource } from './payload';

/** The frame's sandbox: scripts, forms and dialogs. Never allow-same-origin: the page keeps an
 * opaque origin, so it can reach neither Poneva nor the person's session. */
export const PAGE_SANDBOX = 'allow-scripts allow-forms allow-modals';

/** A message from the page's frame, checked: anything but the two known shapes is ignored. */
export function pageMessage(data: unknown): PageMessage | null {
  if (typeof data !== 'object' || data === null) return null;
  const message = data as Record<string, unknown>;
  if (message.ns !== PAGE_NS) return null;
  if (
    message.type === 'open' &&
    typeof message.url === 'string' &&
    message.url.length <= 2048 &&
    /^https?:\/\//iu.test(message.url)
  )
    return { type: 'open', url: message.url };
  if (
    message.type === 'size' &&
    typeof message.height === 'number' &&
    Number.isFinite(message.height)
  )
    return { type: 'size', height: message.height };
  return null;
}

export function PageFrame({
  html,
  resources,
  origin,
  src,
  title,
  onOpen,
  minHeight = 320,
  maxHeight = 3000,
  fill = false,
  className = 'ws-page-frame',
}: {
  html: string;
  resources: PageResource[];
  /** Where Poneva serves /everywhere/vendor/<sha256> from. */
  origin: string;
  /**
   * Poneva's own address for the page (its signed /everywhere/pages/ path), framed instead of
   * a srcdoc document: a srcdoc frame inherits the embedding page's content policy, and
   * Poneva's own policy runs no inline script.
   */
  src?: string;
  title: string;
  /** Called with an http(s) address the page asked to open. */
  onOpen: (url: string) => void;
  minHeight?: number;
  maxHeight?: number;
  /** Fill the space the parent gives it instead of following the page's own height. */
  fill?: boolean;
  /** The surface's own styling class. */
  className?: string;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [posted, setPosted] = useState<number | null>(null);
  const open = useRef(onOpen);
  open.current = onOpen;
  const srcDoc = useMemo(
    () => (src ? undefined : pageDocument({ html, resources, origin })),
    [src, html, resources, origin],
  );
  useEffect(() => {
    setPosted(null);
  }, [srcDoc, src]);
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const own = frame.current?.contentWindow;
      if (!own || event.source !== own) return;
      const message = pageMessage(event.data);
      if (message?.type === 'open') open.current(message.url);
      else if (message?.type === 'size') setPosted(Math.ceil(message.height));
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
  const height = Math.min(Math.max(posted ?? minHeight, minHeight), maxHeight);
  return (
    <iframe
      ref={frame}
      className={`${className}${fill ? ' is-fill' : ''}`}
      title={title}
      sandbox={PAGE_SANDBOX}
      referrerPolicy="no-referrer"
      {...(src ? { src } : { srcDoc })}
      style={fill ? undefined : { height }}
    />
  );
}
